import type { GeometryKey } from '../locales/keys';

export type CircleMode =
  | 'inscribed'
  | 'tangent'
  | 'cyclic-quad'
  | 'alternate-segment'
  | 'power-of-point';

export interface CirclePreset {
  id: string;
  mode: CircleMode;
  labelKey: GeometryKey;
  advancedOnly: boolean;
  /** For inscribed mode: angle (degrees) of point A on circle */
  angleA?: number;
  /** For inscribed mode: angle (degrees) of point B on circle */
  angleB?: number;
  /** For inscribed mode: angle (degrees) of inscribed vertex P */
  angleP?: number;
  /** For power-of-point: position of P as fraction of radius (negative = outside) */
  powerPointOffset?: number;
}

export const CIRCLE_PRESETS: readonly CirclePreset[] = [
  {
    id: 'thales',
    mode: 'inscribed',
    labelKey: 'circle.preset.thales',
    advancedOnly: false,
    angleA: 0,
    angleB: 180,
    angleP: 90,
  },
  {
    id: 'minor-arc',
    mode: 'inscribed',
    labelKey: 'circle.preset.minor-arc',
    advancedOnly: false,
    angleA: 30,
    angleB: 90,
    angleP: 200,
  },
  {
    id: 'crossed-chords',
    mode: 'power-of-point',
    labelKey: 'circle.preset.crossed-chords',
    advancedOnly: true,
    powerPointOffset: 0.4,
  },
  {
    id: 'tangent-secant',
    mode: 'power-of-point',
    labelKey: 'circle.preset.tangent-secant',
    advancedOnly: true,
    powerPointOffset: 1.8,
  },
];

export function isValidCirclePreset(p: CirclePreset): boolean {
  if (typeof p.id !== 'string' || p.id.length === 0) return false;
  const validModes: CircleMode[] = [
    'inscribed',
    'tangent',
    'cyclic-quad',
    'alternate-segment',
    'power-of-point',
  ];
  if (!validModes.includes(p.mode)) return false;
  if (typeof p.labelKey !== 'string' || p.labelKey.length === 0) return false;
  if (typeof p.advancedOnly !== 'boolean') return false;
  if (p.angleA !== undefined && typeof p.angleA !== 'number') return false;
  if (p.angleB !== undefined && typeof p.angleB !== 'number') return false;
  if (p.angleP !== undefined && typeof p.angleP !== 'number') return false;
  if (p.powerPointOffset !== undefined && typeof p.powerPointOffset !== 'number') return false;
  return true;
}

export function validateCirclePresetIds(presets: readonly CirclePreset[]): boolean {
  const ids = new Set<string>();
  for (const preset of presets) {
    if (ids.has(preset.id)) return false;
    ids.add(preset.id);
  }
  return true;
}
